<template>
  <VTextField
    v-model="ccNumber"
    class="flex-column ns-credit-card"
    name="cardNumber"
    type="tel"
    validate-on-blur
    :label="t.fieldLabel"
    :mask="cardMask"
    maxlength="16"
    :rules="isValidNumber"
    :placeholder="t.placeholder"
    :disabled="fieldDisabled"
    @blur="validateLuhn()"
    @input="updatedCardNumber"
  >
    <template v-slot:append-outer>
      <img
        v-for="(card, index) in ccOptions"
        :key="index"
        class="p-icon"
        :class="{ 'display-none': paymentTypeId !== card.id }"
        :src="getCardIcon(card.id)"
        :alt="card.localizedName"
      />
    </template>
  </VTextField>
</template>

<script>
/**
 * Original Author: Paul Roberts
 * Original Team: Mavericks
 */
import { VTextField } from 'vuetify/lib';
import UTIL_MIXIN from './utilMixin';
import LuhnCheck from './luhnCheck.js';
import PaymentType from './paymentType.js';

export default {
  name: 'NsCreditCard',
  components: {
    VTextField,
  },
  mixins: [UTIL_MIXIN],
  props: {
    /**
     * Available Payments for the market
     */
    availablePayments: {
      type: Array,
      default() {
        return [];
      },
    },
    /**
     * Translations as a key:value object
     */
    translations: {
      type: Object,
      default() {
        return {
          fieldLabel: 'Credit Card Number',
          fieldRequired: 'Required',
          notRecognizedCard: 'Not a recognized credit card',
          invalidJcbNumber:
            "We don't currently support JCB cards that begin with 357401",
          placeholder: '    -    -    -    ',
        };
      },
    },
    /**
     * Field Disabled (for edit mode)
     */
    fieldDisabled: {
      type: Boolean,
      default: false,
    },
    value: {
      type: String,
      default() {
        return '';
      },
    },
    /**
     * CX16-3221 Feature to prevent certain JCB Bin numbers
     */
    preventJcbBin: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mask: '',
      isValidNumber: [],
      paymentTypeId: null,
      ccNumber: this.value,
    };
  },
  computed: {
    t() {
      return this.translations;
    },
    ccOptions() {
      if (this.availablePayments && this.availablePayments.length > 0) {
        return this.availablePayments.filter((card) => {
          return card.formName !== 'emptyPayment' ? card : null;
        });
      }
      return [];
    },
    cardMask() {
      return this.setMask();
    },
  },
  watch: {
    paymentTypeId(newVal, oldVal) {
      if (newVal !== oldVal) {
        // when the paymentTypeId changes emit the value
        this.$emit('paymentTypeId', newVal);
      }
    },
    value(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.ccNumber = newVal;
      }
    },
    t() {
      if (Object.keys(this.t).length > 0) {
        this.initValidatorRules();
      }
    },
    availablePayments() {
      this.setPaymentTypeId();
    },
  },
  mounted() {
    this.initValidatorRules();
  },
  methods: {
    setMask() {
      // default mask
      let mask = '';
      // The Japanese Kana keyboard on IOS does not work with vuetify mask
      if (!(this.isIOS() && window.navigator.language.includes('ja'))) {
        // If the field is disabled then the card number will have XXXX in it so we can't match
        if (this.ccNumber && !this.fieldDisabled) {
          if (this.ccNumber.match(/^3[47]/)) {
            mask = '####-######-#####';
          } else if (this.ccNumber.match(/^3(?:0[0-5]|[68][0-9])/)) {
            mask = '####-######-####';
          } else if (
            '97880'.startsWith(this.ccNumber) ||
            this.ccNumber.startsWith('97880')
          ) {
            mask = '###############';
          } else {
            mask = '####-####-####-####';
          }
        } else {
          switch (this.paymentTypeId) {
            case PaymentType.DINE:
            case PaymentType.AMEX:
              mask = 'NNNN-NNNNNN-####';
              break;
            default:
              mask = 'NNNN-NNNN-NNNN-####';
          }
        }
      }
      return mask;
    },

    getPmtId(optionIds = []) {
      const optionId = optionIds.find((id) =>
        this.ccOptions.some((o) => o.id === id)
      );
      return optionId >= 0 ? optionId : '';
    },

    setPaymentTypeId() {
      if (this.ccNumber) {
        // if (this.ccNumber.startsWith(4580)) {
        //   // ISRA
        //   this.paymentTypeId = this.getPmtId([PaymentType.ISRA]);
        // } else
        if (
          this.ccNumber.startsWith(35) ||
          this.ccNumber.startsWith(3088) ||
          this.ccNumber.startsWith(3096) ||
          this.ccNumber.startsWith(3112) ||
          this.ccNumber.startsWith(3158) ||
          this.ccNumber.startsWith(3337) ||
          this.ccNumber.startsWith(3338) ||
          this.ccNumber.startsWith(2131) ||
          this.ccNumber.startsWith(1800) ||
          this.ccNumber.startsWith(2131)
        ) {
          // JCB
          this.paymentTypeId = this.getPmtId([
            PaymentType.JCB,
            PaymentType.CBLU,
          ]);
        } else if (this.ccNumber.startsWith(4)) {
          // VISA, CBLU, SIMV
          this.paymentTypeId = this.getPmtId([
            PaymentType.VISA,
            PaymentType.SIMV,
            PaymentType.CBLU,
          ]);
        } else if (this.ccNumber.match(/^51|^52|^53|^54|^55|^2/)) {
          // Mastercard, CBLU
          this.paymentTypeId = this.getPmtId([
            PaymentType.MC,
            PaymentType.CBLU,
          ]);
        } else if (this.ccNumber.startsWith('6')) {
          // Discover
          this.paymentTypeId = this.getPmtId([
            PaymentType.DISC,
            PaymentType.CBLU,
          ]);
          //3088, 3096, 3112, 3158, 3337, 3338.2131 or 1800 3525 to 3589
        } else if (this.ccNumber.match(/^3[47]/)) {
          // AMEX
          this.paymentTypeId = this.getPmtId([
            PaymentType.AMEX,
            PaymentType.CBLU,
          ]);
        } else if (this.ccNumber.match(/^3(?:0[0-5]|[68][0-9])/)) {
          // Diners Card
          this.paymentTypeId = this.getPmtId([
            PaymentType.DINE,
            PaymentType.CBLU,
          ]);
        } else if (
          '97880'.startsWith(this.ccNumber) ||
          this.ccNumber.startsWith('97880')
        ) {
          // Cedyna
          this.paymentTypeId = this.getPmtId([
            PaymentType.CEDN,
            PaymentType.CBLU,
          ]);
        } else {
          this.paymentTypeId = '';
        }
      } else {
        this.paymentTypeId = '';
      }
    },

    initValidatorRules() {
      const isValidNumber = [
        () => this.fieldDisabled,
        (v) => !!v || this.t.fieldRequired,
      ];

      if (this.preventJcbBin) {
        isValidNumber.push(
          (v) => !v.startsWith(357401) || this.t.invalidJcbNumber
        );
      }
      this.isValidNumber = isValidNumber;
    },

    validateLuhn() {
      let validValue = (this.availablePayments || []).some(
        (p) => p.id === this.paymentTypeId
      );
      validValue =
        this.paymentTypeId === PaymentType.CBLU
          ? validValue && LuhnCheck.simpleLuhnCheck(this.ccNumber)
          : validValue &&
            LuhnCheck.luhnCheck(this.ccNumber, this.paymentTypeId);
      const isValidNumber = [
        () => this.fieldDisabled,
        (v) => !!v || this.t.fieldRequired,
        () => validValue || this.t.notRecognizedCard,
      ];

      if (this.preventJcbBin) {
        isValidNumber.push(
          (v) => !v.startsWith(357401) || this.t.invalidJcbNumber
        );
      }

      this.isValidNumber = isValidNumber;
    },
    getCardIcon(id) {
      return `https://www.nuskin.com/content/dam/apps/checkout/creditcards/v2/CC-${id}.png`;
    },
    updatedCardNumber() {
      this.$emit('input', this.ccNumber);
      this.setPaymentTypeId();
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/vuetifyOverrides";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";

.ns-credit-card {
  font-family: map.get(typographyVariables.$typography, font-family-primary);
  position: relative;

  ::v-deep {
    .v-text-field {
      position: relative;
    }

    input[type='tel']:focus {
      border: 1px solid map.get(colors.$theme, 'accent-color-b') !important;
    }
    .v-input__append-inner {
      top: auto;
      right: 5px;
      bottom: 7px;
    }

    .v-input__append-outer {
      position: absolute;
      right: 0;
      margin: 0 0 0 5px;
      max-height: 20px;
    }
  }

  .icon {
    color: map.get(colors.$theme, 'primary') !important;
  }

  .p-icon {
    margin-left: 5px;
    transition: filter 0.5s ease-in-out;
    width: 32px;

    &.display-none {
      display: none;
    }
  }
}
</style>
